import { Location } from '@angular/common';
import type { DocumentLinkDialogData, DocumentLinkUrlState } from './document-link.model';
import { clearDocumentLinkSidebarState, saveDocumentLinkSidebarState } from './document-link-context.util';

/** Browser URL/session restoration, including reload-aware cleanup. */
export class DocumentLinkSidebarContext {
  private unloading = false;
  private readonly markUnloading = () => {
    this.unloading = true;
  };
  constructor(
    private readonly data: DocumentLinkDialogData,
    private readonly location: Location
  ) {}
  initialize(): void {
    window.addEventListener('beforeunload', this.markUnloading);
    this.update({ linkSidebar: 'open', documentId: this.data.linkedDocumentId ?? null });
    saveDocumentLinkSidebarState({
      linkSidebar: 'open',
      linkDialog: null,
      documentId: this.data.linkedDocumentId ?? null,
      category: this.data.category ?? null,
      linkedArticleId: this.data.linkedArticleId ?? null,
      originTitle: this.data.originTitle ?? null,
    });
  }
  destroy(): void {
    window.removeEventListener('beforeunload', this.markUnloading);
    if (!this.unloading) {
      this.update({ linkSidebar: null, linkDialog: null, documentId: null });
      clearDocumentLinkSidebarState();
    }
  }
  update(state: DocumentLinkUrlState) {
    try {
      const currentUrl = this.location.path(true);
      const hashIndex = currentUrl.indexOf('#');
      const baseWithQuery = hashIndex === -1 ? currentUrl : currentUrl.slice(0, hashIndex);
      const hash = hashIndex === -1 ? '' : currentUrl.slice(hashIndex);
      const [basePath, queryString = ''] = baseWithQuery.split('?', 2);
      const searchParams = new URLSearchParams(queryString);
      for (const [key, value] of Object.entries(state)) {
        const queryKey = key === 'documentId' ? 'linkId' : key;
        if (value === null || value === undefined || value === '') {
          searchParams.delete(queryKey);
        } else {
          searchParams.set(queryKey, value);
        }
      }
      const shouldPersistContext = (searchParams.get('linkSidebar') as string | null) === 'open';
      if (shouldPersistContext) {
        if (this.data.category) {
          searchParams.set('linkCategory', this.data.category);
        } else {
          searchParams.delete('linkCategory');
        }
        if (this.data.linkedArticleId) {
          searchParams.set('linkArticleId', this.data.linkedArticleId);
        } else {
          searchParams.delete('linkArticleId');
        }
      } else {
        searchParams.delete('linkCategory');
        searchParams.delete('linkArticleId');
      }
      const nextQuery = searchParams.toString();
      const nextUrl = `${basePath}${nextQuery ? `?${nextQuery}` : ''}${hash}`;
      this.location.replaceState(nextUrl);
      if (state.linkSidebar === null && state.linkDialog === null && state.documentId === null) {
        clearDocumentLinkSidebarState();
        return;
      }
      saveDocumentLinkSidebarState({
        linkSidebar: (searchParams.get('linkSidebar') as 'open' | null | undefined) ?? null,
        linkDialog: (searchParams.get('linkDialog') as 'preview' | null | undefined) ?? null,
        documentId: (searchParams.get('linkId') as string | null | undefined) ?? null,
        category: this.data.category ?? null,
        linkedArticleId: this.data.linkedArticleId ?? null,
        originTitle: this.data.originTitle ?? null,
      });
    } catch {
      // ignore url update failures
    }
  }
}
